import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleRight, faGripLines, faLock, faPen, faTrash } from '@fortawesome/free-solid-svg-icons';
import { iconMapping } from 'services/utils/iconMapping';
import { Avatar } from 'components/layout/Avatar';
import { KnowledgeBaseArticleListItem, KnowledgeBaseAuthor, KnowledgeBaseGroup, describeAuthors, groupIconKey, resolveMediaUrl } from './KnowledgeBaseTypes';

// The actions a card shows in management mode
interface CardAdminActions {
    onEdit?: () => void;
    onDelete?: () => void;
    dragHandleProps?: Record<string, any>;
}

// Shows the profile picture of an author or the colored initials avatar of the platform
export const KnowledgeBaseAvatar: React.FC<{ author: KnowledgeBaseAuthor, size?: 'small' | 'large' }> = ({ author, size = 'small' }) => {
    const [imageFailed, setImageFailed] = useState(false);
    const avatarUrl = resolveMediaUrl(author.avatar);
    return (
        <div className={`kb-avatar kb-avatar-${size}`} title={author.name}>
            {avatarUrl && !imageFailed
                ? <div className='kb-avatar-picture' style={{ backgroundImage: `url(${avatarUrl})` }}>
                    <img src={avatarUrl} alt='' style={{ display: 'none' }} onError={() => setImageFailed(true)} />
                </div>
                : <Avatar name={author.name} size={size === 'large' ? '2.5rem' : '1.75rem'} />
            }
        </div>
    );
};

// Shows the stacked avatars of the authors with the article count and the written by line
export const KnowledgeBaseAuthorsLine: React.FC<{ authors: KnowledgeBaseAuthor[], articleCount: number }> = ({ authors, articleCount }) => {
    const { t } = useTranslation();
    return (
        <div className='kb-card-authors'>
            {authors.length > 0 && (
                <div className='kb-avatars'>
                    {authors.slice(0, 3).map(author => <KnowledgeBaseAvatar key={author.id} author={author} />)}
                    {authors.length > 3 && <div className='kb-avatar kb-avatar-small kb-avatar-more'><span>+{authors.length - 3}</span></div>}
                </div>
            )}
            <div className='kb-card-authors-text'>
                <div className='kb-card-collection-count'>{t('knowledge_base.public.collection_count', { count: articleCount })}</div>
                {authors.length > 0 && <div className='kb-card-written-by'>{describeAuthors(authors, t)}</div>}
            </div>
        </div>
    );
};

interface GroupCardProps {
    group: KnowledgeBaseGroup;
    onClick: () => void;
    adminActions?: CardAdminActions;
}

// Shows a knowledge base group as a full width card with its icon, authors and article count
export const KnowledgeBaseGroupCard: React.FC<GroupCardProps> = ({ group, onClick, adminActions }) => {
    const { t } = useTranslation();

    // Stops the card click when an action is clicked
    const handleAction = (event: React.MouseEvent, action?: () => void) => {
        event.stopPropagation();
        if (action) action();
    };

    return (
        <div className={`kb-card kb-group-card ${adminActions ? 'kb-card-admin' : ''}`} onClick={onClick} role='button' tabIndex={0}>
            {adminActions?.dragHandleProps && (
                <div className='kb-card-drag-handle' {...adminActions.dragHandleProps} onClick={(event) => event.stopPropagation()}>
                    <FontAwesomeIcon icon={faGripLines} />
                </div>
            )}
            <div className='kb-group-icon'>
                <FontAwesomeIcon icon={iconMapping(groupIconKey(group.icon))} />
            </div>
            <div className='kb-card-body'>
                <div className='kb-card-title'>{group.name}</div>
                {group.description && <div className='kb-card-description'>{group.description}</div>}
                {adminActions
                    ? <div className='kb-card-meta'>{t('knowledge_base.general.article_count', { count: group.article_count })}</div>
                    : <KnowledgeBaseAuthorsLine authors={group.authors ?? []} articleCount={group.article_count} />
                }
            </div>
            {adminActions ? (
                <div className='kb-card-actions'>
                    {adminActions.onEdit && (
                        <button type='button' className='kb-icon-button' onClick={(event) => handleAction(event, adminActions.onEdit)} title={t('general.edit')}>
                            <FontAwesomeIcon icon={faPen} />
                        </button>
                    )}
                    {adminActions.onDelete && (
                        <button type='button' className='kb-icon-button' onClick={(event) => handleAction(event, adminActions.onDelete)} title={t('general.delete')}>
                            <FontAwesomeIcon icon={faTrash} />
                        </button>
                    )}
                </div>
            ) : (
                <div className='kb-card-chevron'><FontAwesomeIcon icon={faAngleRight} /></div>
            )}
        </div>
    );
};

interface ArticleCardProps {
    article: KnowledgeBaseArticleListItem;
    onClick: () => void;
    adminActions?: CardAdminActions;
}

// Shows an article as a full width card below its section title
export const KnowledgeBaseArticleCard: React.FC<ArticleCardProps> = ({ article, onClick, adminActions }) => {
    const { t } = useTranslation();

    return (
        <div className='kb-card kb-article-card' onClick={onClick} role='button' tabIndex={0}>
            {adminActions?.dragHandleProps && (
                <div className='kb-card-drag-handle' {...adminActions.dragHandleProps} onClick={(event) => event.stopPropagation()}>
                    <FontAwesomeIcon icon={faGripLines} />
                </div>
            )}
            <div className='kb-card-body'>
                <div className='kb-card-title'>
                    {article.subject || t('knowledge_base.article.untitled')}
                    {article.access === 'restricted' && <FontAwesomeIcon icon={faLock} className='kb-lock-icon' title={t('knowledge_base.article.access_restricted')} />}
                </div>
                {article.description && <div className='kb-card-description'>{article.description}</div>}
            </div>
            {adminActions && article.status && (
                <span className={`kb-status-badge kb-status-${article.status}`}>{t(`knowledge_base.article.status_${article.status}`)}</span>
            )}
            <div className='kb-card-chevron'><FontAwesomeIcon icon={faAngleRight} /></div>
        </div>
    );
};
